import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { DecodedValueMap } from 'use-query-params';

import { fetchPurchaseItemOptionsV2 } from 'apis/merchant-gateway/merchant-analytics/api';
import { PurchaseItem } from 'apis/merchant-gateway/merchant-analytics/types';
import { isAnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import useCursorPagination from 'common/hooks/useCursorPagination';
import { IsoDateRange } from 'modules/purchases/FilterDateRangePicker';
import { PurchasesQueryConfig } from 'modules/purchases/purchasesQueryConfig';
import { useVenueContext } from 'modules/venue/VenueContext';

export const useItemOptions = ({
  selectedItem,
  dateRange,
  query,
}: {
  selectedItem: PurchaseItem;
  dateRange: IsoDateRange;
  query: DecodedValueMap<PurchasesQueryConfig>;
}) => {
  const venue = useVenueContext();
  const intl = useIntl();

  const {
    data,
    isError: isError,
    isFetching: isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCursorPagination(
    `/merchant-analytics-api/venues/${venue.id}/items/${selectedItem.itemId}/options_totals`,
    {
      venueId: venue.id,
      itemId: selectedItem.itemId,
      locale: intl.locale,
      startDate: DateTime.fromISO(dateRange.from, { zone: venue.timezone }).toISODate(),
      endDate: DateTime.fromISO(dateRange.to, { zone: venue.timezone }).plus({ days: 1 }).toISODate(),
      limit: 100,
      sortField: 'COUNT',
      sortOrder: 'DESC',
      ...(query && query.filters && { filters: query.filters.split(',').filter(isAnalyticsFilter) }),
    },
    fetchPurchaseItemOptionsV2,
    { refetchOnMount: false, refetchOnWindowFocus: false, retry: false },
  );

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  return {
    isError,
    isLoading: hasNextPage || isFetchingNextPage || isLoading,
    data: data.flat(),
  };
};
